'use client'

import { useState, useEffect } from 'react'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react'
import {
  BellIcon,
  CalendarIcon,
  HomeIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import logo from '../assets/wonderstood-high-resolution-logo-grayscale-transparent.png'
import Dashboard from './Dashboard'
import FindProfessionals from './FindProfessionals'
import Favorites from './Favorites'
import Meetings from './Meetings'
import UserProfile from './UserProfile'
import BillingPayments from './BillingPayments'
import ProfileCompletionPrompt from './ProfileCompletionPrompt'
import NotificationsModal from './NotificationsModal'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { useNotifications } from '../hooks/useNotifications'
import { useSessionTimeout } from '../hooks/useSessionTimeout'
// Theme context kept for compatibility but we force dark mode
import { useTheme } from '../contexts/ThemeContext'
import { logOut } from '../lib/auth'
import SessionTimeoutWarning from './SessionTimeoutWarning'
import { formatNotificationTime, getUserNotifications, markAllNotificationsAsRead } from '../lib/notificationService'
import { checkProfileCompletionStatus, updateUserDocument, updateEmailVerificationStatus } from '../lib/userService'
import { applyActionCode, checkActionCode } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { initializePushNotifications, setupPushNotificationListeners, handleNotificationNavigation } from '../services/pushNotificationService'
import { isNativePlatform } from '../utils/platform'

// Heart icon for Favorites
const HeartIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

// Base navigation items available to all users
const baseNavigation = [
  { name: 'Dashboard', href: '#', icon: HomeIcon, current: true },
  { name: 'Find Engineers', href: '#', icon: UsersIcon, current: false },
  { name: 'Favorites', href: '#', icon: HeartIcon, current: false },
  { name: 'Meetings', href: '#', icon: CalendarIcon, current: false },
]

// Professional-only navigation items
const professionalNavigation: typeof baseNavigation = [
  // Analytics removed
]
const userNavigation = [
  { name: 'Settings', href: '#', action: 'profile' },
  { name: 'Sign out', href: '/', action: 'signout' },
]

function classNames(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Separate component for email verification to avoid hook ordering issues
function EmailVerificationFlow({ oobCode }: { oobCode: string }) {
  const navigate = useNavigate()

  useEffect(() => {
    const runVerification = async () => {
      try {
        console.log('📧 Starting email verification process...')

        // Wait for auth to initialize
        await new Promise<void>((resolve) => {
          if (auth.currentUser) {
            resolve()
          } else {
            let resolved = false
            const unsubscribe = auth.onAuthStateChanged(() => {
              if (!resolved) {
                resolved = true
                unsubscribe()
                setTimeout(resolve, 200)
              }
            })
            setTimeout(() => {
              if (!resolved) {
                resolved = true
                unsubscribe()
                resolve()
              }
            }, 3000)
          }
        })

        // Check the action code to get the email address
        let verificationEmail: string | null = null
        try {
          const actionCodeInfo = await checkActionCode(auth, oobCode)
          verificationEmail = actionCodeInfo.data.email ?? null
        } catch (checkError) {
          console.warn('Could not check action code:', checkError)
        }

        // Apply the verification code
        await applyActionCode(auth, oobCode)
        console.log('✅ Verification code applied successfully')

        await new Promise(resolve => setTimeout(resolve, 500))

        const currentUser = auth.currentUser
        if (currentUser) {
          await currentUser.reload()
          await updateEmailVerificationStatus(currentUser.uid, true)
          localStorage.setItem('emailVerified', 'true')

          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage({ type: 'EMAIL_VERIFIED' }, window.location.origin)
              await new Promise(resolve => setTimeout(resolve, 500))
              window.close()
            } catch {
              navigate('/app?setup=true', { replace: true })
            }
          } else {
            navigate('/app?setup=true', { replace: true })
          }
        } else {
          localStorage.setItem('emailVerified', 'true')
          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage({ type: 'EMAIL_VERIFIED_NO_SESSION', email: verificationEmail }, window.location.origin)
              await new Promise(resolve => setTimeout(resolve, 500))
              window.close()
            } catch { /* fallthrough */ }
          }
          const loginUrl = verificationEmail
            ? `/login?verified=true&email=${encodeURIComponent(verificationEmail)}`
            : '/login?verified=true'
          window.location.href = loginUrl
        }
      } catch (error) {
        console.error('❌ Email verification failed:', error)
        const errorCode = (error as any)?.code
        if (errorCode === 'auth/invalid-action-code' || errorCode === 'auth/expired-action-code') {
          window.location.href = '/login?error=expired'
        } else {
          window.location.href = '/login?verificationError=true'
        }
      }
    }

    runVerification()
  }, [oobCode, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/40 mx-auto"></div>
        <p className="mt-4 text-white/60">Verifying your email...</p>
        <p className="mt-2 text-xs text-white/30">This should only take a moment</p>
      </div>
    </div>
  )
}

export default function MainApp() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check for Firebase auth action URLs (early return happens after all hooks)
  const mode = searchParams.get('mode')
  const oobCode = searchParams.get('oobCode')

  // Map URL params to view names
  const urlToViewName: { [key: string]: string } = {
    'dashboard': 'Dashboard',
    'find-professionals': 'Find Engineers',
    'favorites': 'Favorites',
    'meetings': 'Meetings',
    'billing-payments': 'Stripe Dashboard',
    'user-profile': 'User Profile',
  }

  const viewToUrlParam: { [key: string]: string } = {
    'Dashboard': 'dashboard',
    'Find Engineers': 'find-professionals',
    'Favorites': 'favorites',
    'Meetings': 'meetings',
    'Stripe Dashboard': 'billing-payments',
    'User Profile': 'user-profile',
  }

  // Get current view from URL, default to Dashboard
  const viewParam = searchParams.get('view') || 'dashboard'
  const currentView = urlToViewName[viewParam] || 'Dashboard'

  const [openAvailabilityOnNav, setOpenAvailabilityOnNav] = useState(false)
  const [showProfilePrompt, setShowProfilePrompt] = useState(false)
  const [stripeSuccessMessage, setStripeSuccessMessage] = useState('')
  const [showNotificationsModal, setShowNotificationsModal] = useState(false)
  const [allNotifications, setAllNotifications] = useState<any[]>([])
  const [loadingAllNotifications, setLoadingAllNotifications] = useState(false)

  // Session timeout (desktop only)
  const { showWarning, timeRemaining, extendSession, isEnabled } = useSessionTimeout()

  // Function to change view (updates URL instead of state)
  const setCurrentView = (view: string) => {
    // Handle special view variants
    if (view === 'User Profile:availability') {
      setOpenAvailabilityOnNav(true)
      const urlParam = viewToUrlParam['User Profile'] || 'user-profile'
      navigate(`/app?view=${urlParam}`, { replace: false })
      return
    }
    setOpenAvailabilityOnNav(false)
    const urlParam = viewToUrlParam[view] || view.toLowerCase().replace(/\s+/g, '-')
    navigate(`/app?view=${urlParam}`, { replace: false })
  }


  const { user: authUser, loading: authLoading } = useAuth()
  const { userProfile, loading: profileLoading } = useUserProfile()
  const { setThemeFromFirestore } = useTheme()

  // Real-time notifications data (must be called before conditional returns)
  const { notifications, unreadCount, loading: notificationsLoading, markAsRead } = useNotifications(10)

  // Redirect to home if not authenticated
  // BUT don't redirect if this is an email verification link or setup flow
  useEffect(() => {
    const mode = searchParams.get('mode')
    const oobCode = searchParams.get('oobCode')
    const setup = searchParams.get('setup')

    // Skip auth redirect if this is email verification or setup flow
    if ((mode === 'verifyEmail' && oobCode) || setup === 'true') {
      return
    }

    // Only redirect if auth loading is complete AND there's no user
    // Don't use a timer - wait for auth state to actually resolve
    if (!authLoading && !authUser) {
      // On native platforms, go directly to login screen
      // On web, show the marketing landing page
      const redirectPath = isNativePlatform() ? '/login' : '/'
      navigate(redirectPath)
      return
    }
  }, [authUser, authLoading, navigate, searchParams])

  // Load theme preference from Firestore when user profile loads (initial load only)
  useEffect(() => {
    if (userProfile && !profileLoading && userProfile.themePreference !== undefined) {
      setThemeFromFirestore(userProfile.themePreference)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.themePreference, profileLoading])

  // Check profile completion status when user profile loads
  useEffect(() => {
    if (userProfile && !profileLoading) {
      const needsCompletion = checkProfileCompletionStatus(userProfile)
      if (needsCompletion) {
        setShowProfilePrompt(true)
      }
    }
  }, [userProfile, profileLoading])

  // Handle Stripe Connect success/error states from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const stripeStatus = urlParams.get('stripe')

    if (stripeStatus === 'success') {
      // Switch to UserProfile view and check if setup was actually completed
      setCurrentView('UserProfile')
      // Only show success message if user is actually onboarded
      // This check will happen after userProfile loads
      if (userProfile?.isStripeOnboarded) {
        setStripeSuccessMessage('🎉 Stripe Connect setup completed successfully! You can now receive payments.')
        setTimeout(() => setStripeSuccessMessage(''), 5000)
      }
      // Clean up URL
      window.history.replaceState({}, '', '/app')
    } else if (stripeStatus === 'refresh') {
      // Switch to UserProfile view for retry (no success message)
      setCurrentView('UserProfile')
      // Clean up URL
      window.history.replaceState({}, '', '/app')
    }
  }, [userProfile?.isStripeOnboarded])

  // Handle setup parameter
  useEffect(() => {
    const setup = searchParams.get('setup')

    if (setup === 'true') {
      setShowProfilePrompt(true)
      // Clean up URL
      window.history.replaceState({}, '', '/app')
    }
  }, [searchParams])

  // Initialize push notifications on mobile platforms
  useEffect(() => {
    if (!authUser || !isNativePlatform()) {
      return
    }

    const initPushNotifications = async () => {
      try {

        // Request permissions and register for push
        await initializePushNotifications(authUser.uid)

        // Set up listeners for incoming notifications
        setupPushNotificationListeners(
          authUser.uid,
          // On notification received (foreground)
          (notification) => {
            console.log('Notification received in foreground:', notification)
            // Could show a toast notification here
          },
          // On notification tapped (background/killed)
          (notification) => {
            console.log('Notification tapped:', notification)
            if (notification.data) {
              handleNotificationNavigation(notification.data, navigate)
            }
          }
        )

      } catch (error) {
        console.error('Failed to initialize push notifications:', error)
      }
    }

    initPushNotifications()
  }, [authUser, navigate])

  // Handle Firebase auth action URLs (after all hooks are declared)
  if (mode === 'resetPassword' && oobCode) {
    window.location.href = `/reset-password?${searchParams.toString()}`
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/40 mx-auto"></div>
          <p className="mt-4 text-sm text-white/50">Redirecting...</p>
        </div>
      </div>
    )
  }

  if (mode === 'verifyEmail' && oobCode) {
    return <EmailVerificationFlow oobCode={oobCode} />
  }

  // Show loading state while profile is loading or if no auth user
  if (!authUser || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/40"></div>
      </div>
    )
  }

  // If no user profile found, show error or redirect to registration
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="text-lg font-medium text-white">Profile not found</h2>
          <p className="mt-2 text-white/50">Please complete your registration.</p>
          <button
            onClick={() => navigate('/register')}
            className="mt-4 rounded-full bg-white text-black px-6 py-2.5 text-sm font-semibold hover:scale-[1.02] transition-all"
          >
            Complete Registration
          </button>
        </div>
      </div>
    )
  }

  // User object for compatibility with existing code
  const user = {
    name: userProfile.displayName || userProfile.firstName + ' ' + userProfile.lastName,
    email: userProfile.email,
    accountType: userProfile.userType === 'professional' ? 'Professional' : 'Client'
  }

  // Build navigation based on user account type
  const getNavigation = () => {
    let navigation = [...baseNavigation]
    if (user.accountType === 'Professional') {
      navigation = [...navigation, ...professionalNavigation]
    }
    return navigation
  }

  // Update navigation current state based on currentView
  const navigationWithCurrent = getNavigation().map(item => ({
    ...item,
    current: item.name === currentView
  }))

  const handleNavigation = (viewName: string) => {
    setCurrentView(viewName)
  }

  const handleUserMenuClick = async (action: string) => {
    if (action === 'profile') {
      setCurrentView('User Profile')
    } else if (action === 'signout') {
      try {
        // Sign out from Firebase Auth
        await logOut()
        // Clear any local storage
        localStorage.removeItem('isAuthenticated')
        localStorage.removeItem('userEmail')
        // Navigate to landing page
        navigate('/')
      } catch (error) {
        console.error('Error signing out:', error)
      }
    }
  }

  const handleQuickSetup = async (data: any) => {
    if (!authUser) return

    try {
      // Construct displayName from firstName and lastName
      const displayName = `${data.firstName} ${data.lastName}`.trim();

      // Update the user profile with the provided data
      await updateUserDocument(authUser.uid, {
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: displayName, // Add displayName
        timezone: data.timezone,
        bio: data.bio,
        profileCompleted: true
      })

      // Close the modal first
      setShowProfilePrompt(false)

      // Refresh the profile to show updated data
      window.location.reload()
    } catch (error) {
      console.error('Failed to save quick setup data:', error)
      throw error
    }
  }

  const handleMarkAllNotificationsAsRead = async () => {
    if (!authUser) return

    try {
      await markAllNotificationsAsRead(authUser.uid)
      // Refresh the notifications in the modal
      const updatedNotifs = await getUserNotifications(authUser.uid, 100, true)
      setAllNotifications(updatedNotifs)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const renderCurrentView = () => {
    const content = (() => {
      switch (currentView) {
        case 'Find Engineers':
          return <FindProfessionals onNavigateToCalendar={() => setCurrentView('Meetings')} />
        case 'Favorites':
          return <Favorites onNavigateToFindEngineers={() => setCurrentView('Find Engineers')} onNavigateToCalendar={() => setCurrentView('Meetings')} />
        case 'Meetings':
          return <Meetings />
        case 'Stripe Dashboard':
          return (
            <BillingPayments
              userType={user.accountType === 'Professional' ? 'professional' : 'client'}
              userId={`user_${user.name.toLowerCase().replace(' ', '_')}`}
              userEmail={user.email}
            />
          )
        case 'User Profile':
          return <UserProfile openAvailability={openAvailabilityOnNav} />
        case 'Dashboard':
        default:
          return <Dashboard onNavigate={setCurrentView} userProfile={userProfile} />
      }
    })()

    return content
  }

  return (
    <>
      <div className="bg-black">

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-56 lg:flex-col">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-black px-4 border-r border-white/10">
            <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Wonderstood"
                src={logo}
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <nav className="flex flex-1 flex-col pt-2">
              <ul role="list" className="-mx-2 space-y-1">
                {navigationWithCurrent.map((item) => (
                  <li key={item.name}>
                    <button
                      onClick={() => handleNavigation(item.name)}
                      className={classNames(
                        item.current
                          ? 'bg-white/10 text-white'
                          : 'text-white/50 hover:bg-white/5 hover:text-white',
                        'group flex gap-x-3 rounded-xl p-3 text-sm font-medium w-full text-left transition-all duration-200',
                      )}
                    >
                      <item.icon
                        aria-hidden="true"
                        className={classNames(
                          item.current
                            ? 'text-white'
                            : 'text-white/40 group-hover:text-white/70',
                          'size-5 shrink-0',
                        )}
                      />
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-56">
          <div className="fixed top-0 left-0 right-0 lg:left-56 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
            <div className="flex h-16 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8" style={{ marginTop: 'env(safe-area-inset-top)' }}>
              {/* Logo on mobile */}
              <div className="flex items-center lg:hidden">
                <img
                  alt="Wonderstood"
                  src={logo}
                  className="h-6 w-auto brightness-0 invert"
                />
              </div>

              <div className="flex flex-1 justify-end gap-x-4 lg:gap-x-6">
                <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* Notifications dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="relative -m-2.5 p-2.5 text-white/50 hover:text-white transition-colors">
                    <span className="sr-only">View notifications</span>
                    <BellIcon aria-hidden="true" className="size-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-black bg-white rounded-full min-w-[1.25rem] h-5">
                        {unreadCount}
                      </span>
                    )}
                  </MenuButton>
                  <MenuItems
                    className="absolute right-0 z-10 mt-2.5 w-72 origin-top-right rounded-xl py-1.5 shadow-2xl shadow-black/60 ring-1 ring-white/10 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in max-h-[80vh] overflow-y-auto"
                    style={{ background: 'hsl(var(--card))' }}
                  >
                    <div className="px-3.5 py-2 border-b border-white/10">
                      <h3 className="text-[13px] font-medium text-white">
                        Notifications
                      </h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notificationsLoading ? (
                        <div className="px-3.5 py-6 text-center text-white/40">
                          <div className="animate-spin h-5 w-5 border-2 border-white/40 border-t-transparent rounded-full mx-auto"></div>
                          <p className="mt-2 text-xs">Loading...</p>
                        </div>
                      ) : notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <MenuItem key={notification.id}>
                            <div
                              className={`block px-3.5 py-2.5 hover:bg-white/[0.04] cursor-pointer ${
                                !notification.read ? 'bg-white/[0.02]' : ''
                              }`}
                              onClick={async () => {
                                if (!notification.read) {
                                  await markAsRead(notification.id);
                                }
                                if (notification.actionUrl) {
                                  if (notification.actionUrl.includes('/meetings')) {
                                    handleNavigation('Meetings');
                                  } else if (notification.actionUrl.includes('/profile/reviews')) {
                                    handleNavigation('User Profile');
                                  } else if (notification.actionUrl.includes('/payments')) {
                                    handleNavigation('Stripe Dashboard');
                                  }
                                }
                              }}
                            >
                              <div className="flex items-start gap-2.5">
                                <div className="flex-shrink-0 text-sm mt-0.5">
                                  {notification.icon || '🔔'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <p className={`text-[13px] font-medium leading-tight ${
                                      !notification.read ? 'text-white' : 'text-white/60'
                                    }`}>
                                      {notification.title}
                                    </p>
                                    {notification.priority === 'high' || notification.priority === 'urgent' ? (
                                      <div className="ml-2 w-1.5 h-1.5 bg-amber-400 rounded-full shrink-0" />
                                    ) : !notification.read ? (
                                      <div className="ml-2 w-1.5 h-1.5 bg-white rounded-full shrink-0" />
                                    ) : null}
                                  </div>
                                  <p className="mt-0.5 text-[12px] text-white/40 leading-snug">
                                    {notification.message}
                                  </p>
                                  <p className="mt-1 text-[11px] text-white/25">
                                    {formatNotificationTime(notification.createdAt, userProfile?.timezone)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </MenuItem>
                        ))
                      ) : (
                        <div className="px-3.5 py-5 text-center">
                          <p className="text-[13px] text-white/40">No notifications</p>
                        </div>
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="border-t border-white/10">
                        <MenuItem>
                          <button
                            onClick={async () => {
                              setLoadingAllNotifications(true)
                              try {
                                const allNotifs = await getUserNotifications(authUser!.uid, 100, true)
                                setAllNotifications(allNotifs)
                                setShowNotificationsModal(true)
                              } catch (error) {
                                console.error('Error loading all notifications:', error)
                              } finally {
                                setLoadingAllNotifications(false)
                              }
                            }}
                            disabled={loadingAllNotifications}
                            className="block w-full px-3.5 py-2 text-[12px] text-center text-white/50 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
                          >
                            {loadingAllNotifications ? 'Loading...' : 'View all notifications'}
                          </button>
                        </MenuItem>
                      </div>
                    )}
                  </MenuItems>
                </Menu>

                {/* Separator */}
                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-white/10" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="relative flex items-center">
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">Open user menu</span>
                    {userProfile.profileImage ? (
                      <img
                        alt=""
                        src={userProfile.profileImage}
                        className="size-8 rounded-full ring-2 ring-white/20"
                      />
                    ) : (
                      <div className="size-8 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center">
                        <span className="text-xs font-medium text-white/80">
                          {userProfile.firstName && userProfile.lastName
                            ? `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase()
                            : userProfile.displayName
                            ? userProfile.displayName.substring(0, 2).toUpperCase()
                            : 'U'}
                        </span>
                      </div>
                    )}
                    <span className="hidden lg:flex lg:items-center">
                      <span aria-hidden="true" className="ml-4 text-sm font-medium text-white">
                        {userProfile.firstName && userProfile.lastName
                          ? `${userProfile.firstName} ${userProfile.lastName}`
                          : userProfile.displayName || 'User'
                        }
                      </span>
                      <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-white/40" />
                    </span>
                  </MenuButton>
                  <MenuItems
                    className="absolute right-0 z-10 mt-2.5 w-44 origin-top-right rounded-xl py-1.5 shadow-2xl shadow-black/60 ring-1 ring-white/10 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                    style={{ background: 'hsl(var(--card))' }}
                  >
                    {userNavigation.map((item) => (
                      <MenuItem key={item.name}>
                        {item.action === 'profile' ? (
                          <button
                            onClick={() => handleUserMenuClick(item.action)}
                            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-[13px] text-white/70 data-focus:bg-white/[0.06] data-focus:text-white transition-colors"
                          >
                            <svg className="w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" strokeWidth="1.7" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                            {item.name}
                          </button>
                        ) : (
                          <Link
                            to={item.href}
                            onClick={() => handleUserMenuClick(item.action)}
                            className="flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-white/70 data-focus:bg-red-500/10 data-focus:text-red-300 transition-colors"
                          >
                            <svg className="w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" strokeWidth="1.7" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                            </svg>
                            {item.name}
                          </Link>
                        )}
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
            </div>
            </div>
          </div>

          <main className="pb-24 lg:pb-10 bg-black min-h-screen" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}>
            <div className="px-4 sm:px-6 lg:px-8 pt-6">
              {/* Stripe Success Message */}
              {stripeSuccessMessage && (
                <div className="mb-6 rounded-xl bg-green-500/10 p-4 border border-green-500/20">
                  <div className="text-sm font-medium text-green-300">{stripeSuccessMessage}</div>
                </div>
              )}
              {renderCurrentView()}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div
        className="chrome-mobile-nav fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-black/80 backdrop-blur-xl border-t border-white/10 pt-1.5"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <nav className="flex justify-around items-center h-[52px] px-2">
          {navigationWithCurrent.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.name)}
              className={classNames(
                'flex flex-col items-center justify-center flex-1 h-full space-y-1 transition-colors touch-manipulation',
                item.current
                  ? 'text-white'
                  : 'text-white/35 hover:text-white/60'
              )}
            >
              <item.icon
                className={classNames(
                  'h-6 w-6',
                  item.current ? 'text-white' : 'text-white/35'
                )}
              />
              <span className={classNames(
                'text-xs font-medium',
                item.current ? 'text-white' : 'text-white/35'
              )}>
                {item.name === 'Find Engineers' ? 'Explore' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* All Notifications Modal */}
      <NotificationsModal
        open={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        notifications={allNotifications}
        loading={loadingAllNotifications}
        markAsRead={markAsRead}
        onNavigate={handleNavigation}
        markAllAsRead={handleMarkAllNotificationsAsRead}
        userTimezone={userProfile?.timezone}
      />

      {/* Profile Completion Prompt */}
      <ProfileCompletionPrompt
        open={showProfilePrompt}
        onQuickSetup={handleQuickSetup}
        userType={userProfile.userType}
      />

      {/* Session Timeout Warning (Desktop only) */}
      {isEnabled && (
        <SessionTimeoutWarning
          open={showWarning}
          timeRemaining={timeRemaining}
          onExtend={extendSession}
        />
      )}

    </>
  )
}
